import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { LightEnv, OverlayFlags, Renderable } from './types';

import { yeet, memo, useMemo } from '@use-gpu/live';
import { updateMipTextureChain } from '@use-gpu/core';

import { useDeviceContext } from '../providers/device-provider';
import { usePassContext } from '../providers/pass-provider';
import { useRenderContext } from '../providers/render-provider';
import { QueueReconciler } from '../reconcilers/index';

import { useInspectable } from '../hooks/useInspectable';

import { useApplyPassBindGroup } from './bindings';
import { getRenderPassDescriptor, drawToPass } from './util';

const {quote} = QueueReconciler;

export type ColorPassProps = PropsWithChildren<{
  env: {
    light?: LightEnv,
  },
  calls: {
    opaque?: Renderable[],
    transparent?: Renderable[],
    debug?: Renderable[],
  },
  overlay?: boolean | OverlayFlags,
  merge?: boolean,
}>;

const label = 'ColorPass';
const LABEL = { label };

const NO_OPS: any[] = [];
const toArray = <T>(x?: T[]): T[] => Array.isArray(x) ? x : NO_OPS;

/** Color render pass.

Draws all opaque calls, then all transparent calls, then all debug wireframes.
*/
export const ColorPass: LC<ColorPassProps> = memo((props: ColorPassProps) => {
  const {
    overlay = false,
    merge = false,
    calls,
    env,
  } = props;

  const resolvedOverlay = (typeof overlay === 'boolean' ? overlay === true : overlay?.color);

  const inspect = useInspectable();

  const device = useDeviceContext();
  const renderContext = useRenderContext();

  const {
    bindGroups: {color: bindGroup},
    views: {view: {cull, uniforms}},
  } = usePassContext();
  const {bindPass, dataBindings} = useApplyPassBindGroup(env, bindGroup, label);

  const opaques      = toArray(calls['opaque']      as Renderable[]);
  const transparents = toArray(calls['transparent'] as Renderable[]);
  const debugs       = toArray(calls['debug']       as Renderable[]);

  const renderPassDescriptor = useMemo(() =>
    getRenderPassDescriptor(renderContext, {overlay: resolvedOverlay, merge, label}),
    [renderContext, resolvedOverlay, merge]);

  const inspected = inspect({
    output: {
      sources: [renderContext.source, renderContext.depth],
    },
    pass: {
      uniforms,
      bindings: dataBindings,
    },
    render: {
      vertices: 0,
      triangles: 0,
    },
  });

  return quote(yeet(() => {
    let vs = 0;
    let ts = 0;

    const countGeometry = (v: number, t: number) => { vs += v; ts += t; };

    const commandEncoder = device.createCommandEncoder(LABEL);
    if (!resolvedOverlay && !merge) renderContext.swap?.();

    const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
    bindPass?.(passEncoder);

    drawToPass(cull, opaques, passEncoder, countGeometry, uniforms);
    drawToPass(cull, transparents, passEncoder, countGeometry, uniforms, -1);
    drawToPass(cull, debugs, passEncoder, countGeometry, uniforms);

    passEncoder.end();

    const command = commandEncoder.finish();
    device.queue.submit([command]);

    // eslint-disable-next-line
    if (renderContext.source && renderContext.source.mips! > 1) updateMipTextureChain(device, renderContext.source);

    inspected.render.vertices = vs;
    inspected.render.triangles = ts;

    return null;
  }));
}, 'ColorPass');
